import "@nectary/components/spinner";
import { MFEProvider, RenderMFE } from "@sinch/mfe-helper-react";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import React from "react";
import { patchRequestsWithAuth } from "../../auth";
import { CookieBanner } from "../../components/CookieBanner/CookieBanner";
import {
  isCookieConsentUnset,
  shouldHandleConsent,
} from "../../components/CookieBanner/consent";
import {
  setDefaultConsent,
  updateGtagConsent,
} from "../../components/CookieBanner/gtag-consent";
import { SidebarContextProvider } from "../../components/NavigationBar/state";
import { ToastManager } from "../../components/ToastManager";
import { CacheDevTools } from "../../helpers/CacheDevTools";
import { ShellIntlProvider, UserlessIntlProvider } from "../../intl";
import { loadMFE } from "../../mfe/mfeLoaders";
import { UserProvider } from "../../user-context";
import { LoadingScreenContainer } from "./LoadingScreenContainer";
import { RoutesGeneratorWithMMOptions } from "./RoutesGeneratorWithMMOptions";
import { ShellWrapper } from "./ShellWrapper";
import { useMmToken } from "./hooks/useMMToken";
import {
  getOnesaasConsent,
  onesaasConsentAtom,
  updateOnesaasConsentCookie,
} from "./onesaas-consent-cookie";
import { useVendorConfigPlugin } from "./vendorConfig";

export const App = () => {
  const token = useMmToken();
  const [appReady, setAppReady] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const vendorPlugin = useVendorConfigPlugin();
  const setConsent = useSetAtom(onesaasConsentAtom);

  useEffect(() => {
    if (vendorPlugin) {
      setDefaultConsent();
      if (shouldHandleConsent(vendorPlugin)) {
        getOnesaasConsent().then((consent) => {
          setConsent(consent);
          if (isCookieConsentUnset(consent)) {
            setShowCookieBanner(true);
          } else {
            updateGtagConsent(
              consent.onesaascookie_acceptance,
              consent.onesaascookie_performance,
              consent.onesaascookie_targeting
            );
          }
        });
      }
    }
  }, [setConsent, vendorPlugin]);

  useEffect(() => {
    if (token) {
      const undoPatchRequests = patchRequestsWithAuth();
      setAppReady(true);
      return undoPatchRequests;
    } else {
      setAppReady(false);
    }
    return;
  }, [token]);

  return appReady && token ? (
    <MFEProvider
      fallback={null}
      initToken={token ? { token, parsedToken: {} } : null}
    >
      <CacheDevTools />
      <UserProvider
        Fallback={() => (
          <LoadingScreenContainer>
            <sinch-spinner size="l" />
          </LoadingScreenContainer>
        )}
      >
        <ShellIntlProvider>
          <SidebarContextProvider>
            <ShellWrapper>
              <RoutesGeneratorWithMMOptions />
              {showCookieBanner && (
                <CookieBanner
                  updateConsent={updateOnesaasConsentCookie}
                  closeBanner={() => setShowCookieBanner(false)}
                />
              )}
            </ShellWrapper>
          </SidebarContextProvider>
          <ToastManager origin="top-right" />
        </ShellIntlProvider>
      </UserProvider>
    </MFEProvider>
  ) : (
    <UserlessIntlProvider>
      <RenderMFE
        basePath={"/"}
        Fallback={() => (
          <LoadingScreenContainer>
            <sinch-spinner size="l" />
          </LoadingScreenContainer>
        )}
        loadRender={() => loadMFE("container", "app")}
      />
      {showCookieBanner && (
        <CookieBanner
          updateConsent={updateOnesaasConsentCookie}
          closeBanner={() => setShowCookieBanner(false)}
        />
      )}
    </UserlessIntlProvider>
  );
};
